/* Member portal API client (GYM-760).
   Separate from the staff apiFetch so member + staff tokens never mix. */

import { ApiError, RateLimitError } from "@/lib/api/client";
import { useMemberAuthStore, type CurrentMember } from "@/lib/auth/memberStore";
import { useGymResolverStore } from "@/lib/stores/gymResolverStore";

// GYM-1684: Runtime-configurable API base — defaults to relative "/api/v1"
// so the existing staff web app is unchanged (regression guard: TC-SSR-01c).
// The store is the integration point; when a resolved gym carries a custom
// API base in its payload (future field), read it here. Cross-origin bases
// require CORS + auth-cookie sign-off from DevOps before enabling.
function getApiBase(): string {
  return useGymResolverStore.getState().apiBase ?? "/api/v1";
}

export interface MemberTokens {
  access: string;
  refresh: string;
}

async function memberRefreshToken(): Promise<string | null> {
  const { refresh } = useMemberAuthStore.getState();
  if (!refresh) return null;
  const res = await fetch(`${getApiBase()}/member/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) {
    useMemberAuthStore.getState().clear();
    return null;
  }
  const data = (await res.json()) as { access: string; refresh?: string };
  useMemberAuthStore.getState().setTokens({
    access: data.access,
    refresh: data.refresh ?? refresh,
  });
  return data.access;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function memberFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  const { access } = useMemberAuthStore.getState();
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (access) {
    headers.set("Authorization", `Bearer ${access}`);
  }
  const res = await fetch(`${getApiBase()}${path}`, { ...init, headers });

  if (res.status === 401 && !retried) {
    const newAccess = await memberRefreshToken();
    if (newAccess) return memberFetch<T>(path, init, true);
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (res.status === 429) {
    const body = data as Record<string, unknown> | null;
    const retryAfter =
      typeof body?.retry_after === "number"
        ? body.retry_after
        : parseInt(res.headers.get("Retry-After") ?? "60", 10);
    const scope = typeof body?.scope === "string" ? body.scope : "unknown";
    throw new RateLimitError(retryAfter, scope, data);
  }

  if (!res.ok) {
    throw new ApiError(res.status, data, `${res.status} ${res.statusText}`);
  }
  return data as T;
}

// --- Endpoints -------------------------------------------------------------

export function memberLogin(email: string, password: string): Promise<MemberTokens> {
  return memberFetch<MemberTokens>("/member/auth/login/", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function memberMe(): Promise<CurrentMember> {
  return memberFetch<CurrentMember>("/member/me/");
}

export function memberPatchLeaderboardOptIn(optIn: boolean): Promise<{ leaderboard_opt_in: boolean }> {
  return memberFetch("/member/leaderboard-opt-in/", {
    method: "PATCH",
    body: JSON.stringify({ leaderboard_opt_in: optIn }),
  });
}

export async function memberRequestPasswordReset(email: string): Promise<void> {
  await memberFetch("/member/auth/password/reset/", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function memberSetPassword(
  uidb64: string,
  token: string,
  newPassword: string,
): Promise<void> {
  await memberFetch("/member/auth/password/set/", {
    method: "POST",
    body: JSON.stringify({ uidb64, token, new_password: newPassword }),
  });
}

export async function memberLogout(): Promise<void> {
  try {
    await memberFetch("/member/auth/logout/", { method: "POST" });
  } finally {
    useMemberAuthStore.getState().clear();
  }
}

// --- Self-service freeze / cancel (GYM-490 / GYM-894) ---------------------

export interface SelfFreezeRequest {
  freeze_start: string; // ISO date YYYY-MM-DD
  freeze_end: string;   // ISO date YYYY-MM-DD
  reason?: string;
  idempotency_key?: string;
}

export type SelfServiceStatus = "approved" | "pending";

export interface SelfFreezeResponse {
  status: SelfServiceStatus;
  message: string;
  /** Present when status=approved */
  subscription_id?: string;
  new_end_date?: string;
  /** Present when status=pending */
  request_id?: string;
  violations?: string[];
}

export type CancelReasonCode =
  | "too_expensive"
  | "moving_away"
  | "not_using"
  | "unhappy_service"
  | "other"
  | "";

export interface SelfCancelRequest {
  reason?: string;
  cancel_reason_code?: CancelReasonCode;
  cancel_reason_text?: string;
  idempotency_key?: string;
}

export interface SelfCancelResponse {
  status: SelfServiceStatus;
  message: string;
  /** Present when status=approved */
  subscription_id?: string;
  credit_note_id?: string | null;
  refund_amount?: string;
  /** Present when status=pending */
  request_id?: string;
  violations?: string[];
}

export function memberSelfFreeze(
  subscriptionId: string,
  data: SelfFreezeRequest,
): Promise<SelfFreezeResponse> {
  return memberFetch<SelfFreezeResponse>(
    `/member/subscriptions/${subscriptionId}/self-freeze/`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function memberSelfCancel(
  subscriptionId: string,
  data: SelfCancelRequest,
): Promise<SelfCancelResponse> {
  return memberFetch<SelfCancelResponse>(
    `/member/subscriptions/${subscriptionId}/self-cancel/`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

// ---------------------------------------------------------------------------
// GYM-495 — Class booking
// ---------------------------------------------------------------------------

export interface ClassOccurrenceSummary {
  id: string;
  template: string | null;
  template_name: string | null;
  template_color: string | null;
  starts_at: string;
  ends_at: string | null;
  capacity: number;
  status: string;
  room: string | null;
  room_name: string | null;
  instructor: string | null;
  instructor_name: string | null;
  booked_count: number;
  waitlist_count: number;
  is_booked: boolean;
  is_waitlisted: boolean;
  is_pending_approval: boolean;
  booking_status: "pending_approval" | "confirmed" | "waitlisted" | "attended" | "no_show" | null;
  booking_id: string | null;
}

export interface ClassBooking {
  id: string;
  occurrence_id: string;
  member_id: string;
  status: "pending_approval" | "confirmed" | "waitlisted" | "cancelled" | "no_show";
  position: number | null;
  created_at: string;
  updated_at: string;
}

/** Shape returned by GET /member/classes/my/ — occurrence + per-member booking fields */
export interface MyBookedOccurrence {
  id: string;                   // occurrence id
  branch: string;
  template: string | null;
  template_name: string | null;
  template_color: string | null;
  template_description: string | null;
  starts_at: string;
  ends_at: string | null;
  capacity: number;
  status: string;               // occurrence status e.g. "scheduled"
  room: string | null;
  room_name: string | null;
  instructor: string | null;
  instructor_name: string | null;
  is_booked: boolean;
  is_waitlisted: boolean;
  is_pending_approval: boolean;
  booking_status: "pending_approval" | "confirmed" | "waitlisted" | "attended" | "no_show" | null;
  booking_id: string | null;
  waitlist_position: number | null;
  booked_count: number;
  waitlist_count: number;
}

export function memberListOccurrences(params?: {
  from?: string;
  to?: string;
}): Promise<ClassOccurrenceSummary[]> {
  const qs = new URLSearchParams();
  if (params?.from) qs.set("from", params.from);
  if (params?.to) qs.set("to", params.to);
  const query = qs.toString() ? `?${qs}` : "";
  return memberFetch<ClassOccurrenceSummary[]>(`/member/classes/occurrences/${query}`);
}

export function memberBookClass(
  occurrenceId: string,
  idempotencyKey?: string,
): Promise<ClassBooking> {
  return memberFetch<ClassBooking>(
    `/member/classes/occurrences/${occurrenceId}/book/`,
    {
      method: "POST",
      body: JSON.stringify({ idempotency_key: idempotencyKey ?? null }),
    },
  );
}

export function memberCancelClassBooking(occurrenceId: string): Promise<ClassBooking> {
  return memberFetch<ClassBooking>(
    `/member/classes/occurrences/${occurrenceId}/cancel/`,
    { method: "POST" },
  );
}

export function memberMyClasses(): Promise<MyBookedOccurrence[]> {
  return memberFetch<MyBookedOccurrence[]>("/member/classes/my/");
}

// ---------------------------------------------------------------------------
// GYM-1073.3 — Member document self-service
// ---------------------------------------------------------------------------

export type DocType = "invoice" | "receipt" | "credit_note" | "statement";

export interface MemberDocumentRow {
  doc_type: DocType;
  doc_id: string;
  reference: string;
  date: string; // ISO date string YYYY-MM-DD
  amount: string;
  currency: string;
  download_token: string;
}

export interface MemberDocumentListResponse {
  results: MemberDocumentRow[];
  count: number;
}

export function memberListDocuments(): Promise<MemberDocumentListResponse> {
  return memberFetch<MemberDocumentListResponse>("/member/documents/");
}

export interface MemberDocumentEmailResponse {
  notification_id: string;
  idempotent: boolean;
  status: string;
}

export function memberEmailDocument(token: string): Promise<MemberDocumentEmailResponse> {
  return memberFetch<MemberDocumentEmailResponse>(`/member/documents/${token}/email/`, {
    method: "POST",
  });
}

/** Returns a URL for inline PDF download (uses the member access token in header). */
export function memberDocumentDownloadUrl(token: string): string {
  return `/api/v1/member/documents/${token}/`;
}

// GYM-933: Penalty standing
export interface PenaltyRecord {
  id: string;
  kind: "late_cancel" | "no_show";
  action: "warn" | "fee" | "suspend";
  step: number;
  applied_at: string;
  suspended_until: string | null;
}

export interface EscalationStep {
  action: "warn" | "fee" | "suspend";
  charge: boolean;
  suspend_days?: number;
}

export interface PenaltyPolicy {
  cancel_window_min: number;
  offence_window_days: number;
  fee_aed: string;
  fee_mode: "strike" | "invoice" | "auto_charge";
  escalation_steps: EscalationStep[];
}

export interface PenaltyStanding {
  offences_in_window: number;
  suspended: boolean;
  active_suspension_until: string | null;
  next_consequence: {
    action: "warn" | "fee" | "suspend";
    charge: boolean;
    suspend_days: number;
    step_index: number;
  };
  penalties: PenaltyRecord[];
  policy: PenaltyPolicy;
}

export function memberPenaltyStanding(): Promise<PenaltyStanding> {
  return memberFetch<PenaltyStanding>("/member/classes/penalties/");
}

// ---------------------------------------------------------------------------
// GYM-1760 — Member Make-up Credits
// ---------------------------------------------------------------------------

export type MemberMakeUpCreditStatus = "issued" | "redeemed" | "expired" | "revoked";

export interface MemberMakeUpCredit {
  id: string;
  source_booking_id: string;
  redeemed_booking_id: string | null;
  status: MemberMakeUpCreditStatus;
  issued_at: string;
  expires_at: string;
  class_name: string;
  occurrence_starts_at: string | null;
}

export interface RedeemMakeUpCreditPayload {
  credit_id: string;
  occurrence_id: string;
}

export function memberListMakeUpCredits(
  creditStatus?: MemberMakeUpCreditStatus,
): Promise<MemberMakeUpCredit[]> {
  const qs = creditStatus ? `?status=${creditStatus}` : "";
  return memberFetch<MemberMakeUpCredit[]>(`/member/classes/makeup-credits/${qs}`);
}

export function memberRedeemMakeUpCredit(
  data: RedeemMakeUpCreditPayload,
): Promise<ClassBooking> {
  return memberFetch<ClassBooking>("/member/classes/makeup-credits/redeem/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// GYM-540: Privacy — data export + erasure
// ---------------------------------------------------------------------------

export type ExportStatus = "pending" | "pending_review" | "running" | "ready" | "expired" | "failed" | "denied";
export type ErasureStatus = "scheduled" | "pending_review" | "cancelled" | "completed" | "deferred" | "failed" | "denied";

export interface DataExportRequest {
  id: string;
  status: ExportStatus;
  requested_at: string;
  expires_at: string | null;
  deadline: string | null;
  download_url: string | null;
  hours_until_expiry: number | null;
  denial_reason: string;
}

export interface ErasureRequest {
  id: string;
  status: ErasureStatus;
  requested_at: string;
  grace_deadline: string;
  deadline: string | null;
  cancelled_at: string | null;
  completed_at: string | null;
  deferred_reason: string;
  denial_reason: string;
}

export interface PrivacyStatus {
  erasure_status: boolean;
  latest_export: DataExportRequest | null;
  active_erasure: ErasureRequest | null;
}

export function memberPrivacyStatus(): Promise<PrivacyStatus> {
  return memberFetch<PrivacyStatus>("/member/account/privacy/");
}

export function memberRequestExport(): Promise<DataExportRequest> {
  return memberFetch<DataExportRequest>("/member/account/export/", { method: "POST" });
}

export function memberExportStatus(id: string): Promise<DataExportRequest> {
  return memberFetch<DataExportRequest>(`/member/account/export/${id}/`);
}

export function memberRequestErasure(): Promise<ErasureRequest | { status: "deferred"; detail: string; erasure_request: ErasureRequest }> {
  return memberFetch("/member/account/erasure/", { method: "POST" });
}

export function memberCancelErasure(id: string): Promise<ErasureRequest> {
  return memberFetch<ErasureRequest>(`/member/account/erasure/${id}/`, { method: "DELETE" });
}

// GYM-1693: Access QR
export interface AccessQrResponse {
  token: string;
  expires_at: string | null;
  ttl_seconds: number;
}

export function getAccessQr(): Promise<AccessQrResponse> {
  return memberFetch<AccessQrResponse>("/member/access-qr/");
}

// ---------------------------------------------------------------------------
// PT Bookings (member portal)
// ---------------------------------------------------------------------------

export type MemberPtStatus = "pending_approval" | "booked" | "checked_in" | "completed" | "cancelled" | "no_show";

export interface MemberPtBooking {
  id: string;
  trainer_name: string | null;
  trainer_id: string | null;
  start_at: string;
  end_at: string;
  status: MemberPtStatus;
  session_burned: boolean;
  notes: string;
  checked_in_at: string | null;
  signed_off_at: string | null;
  has_review: boolean;
  review_stars: number | null;
}

export interface ReviewQuestionOption {
  id: string;
  text_en: string;
  text_ar: string;
  applies_to: "both" | "pt" | "class";
}

export interface ReviewAnswer {
  question_id: string;
  stars: number;
}

export interface SubmitReviewPayload {
  stars?: number;
  comment?: string;
  answers?: ReviewAnswer[];
}

export interface SubmitReviewResponse {
  review_id: string;
  stars: number;
  comment: string;
  status: string;
}

export function memberSubmitReview(
  bookingId: string,
  data: SubmitReviewPayload,
): Promise<SubmitReviewResponse> {
  return memberFetch<SubmitReviewResponse>(
    `/member/pt-bookings/${bookingId}/review/`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

// ---------------------------------------------------------------------------
// Pending reviews — unreviewed completed PT + attended class sessions
// ---------------------------------------------------------------------------

export type PendingReviewType = "pt" | "class";

export interface PendingReviewItem {
  type: PendingReviewType;
  booking_id: string;
  trainer_name: string | null;
  trainer_id: string | null;
  session_name: string | null;
  date: string;
}

export interface PendingReviewsResponse {
  items: PendingReviewItem[];
  questions: ReviewQuestionOption[];
}

export function memberPendingReviews(): Promise<PendingReviewsResponse> {
  return memberFetch<PendingReviewsResponse>("/member/pending-reviews/");
}

export function memberSubmitClassReview(
  bookingId: string,
  data: SubmitReviewPayload,
): Promise<SubmitReviewResponse> {
  return memberFetch<SubmitReviewResponse>(
    `/member/class-bookings/${bookingId}/review/`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export interface MemberPtBookingsResponse {
  upcoming: MemberPtBooking[];
  past: MemberPtBooking[];
}

export interface MemberPtTrainer {
  id: string;
  full_name_en: string;
  full_name_ar: string;
  profile_photo_url: string | null;
  bio_en: string | null;
  bio_ar: string | null;
  specialty_tags: Array<{ id: string; slug: string; label_en: string; label_ar: string }>;
  avg_stars: number | null;
  review_count: number;
  hourly_rate_aed: string | null;
}

export function memberListPtBookings(): Promise<MemberPtBookingsResponse> {
  return memberFetch<MemberPtBookingsResponse>("/member/pt-bookings/");
}

export function memberListPtTrainers(): Promise<MemberPtTrainer[]> {
  return memberFetch<MemberPtTrainer[]>("/member/pt-bookings/trainers/");
}

export function memberCreatePtBooking(data: {
  trainer_id: string;
  start_at: string;
  end_at: string;
  notes?: string;
}): Promise<MemberPtBooking> {
  return memberFetch<MemberPtBooking>("/member/pt-bookings/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface TrainerAvailabilitySlot {
  start_at: string;
  end_at: string;
  is_booked: boolean;
}

export function memberGetTrainerSlots(trainerId: string, date: string): Promise<TrainerAvailabilitySlot[]> {
  return memberFetch<TrainerAvailabilitySlot[]>(`/member/pt-bookings/trainers/${trainerId}/slots/?date=${date}`);
}

// ---------------------------------------------------------------------------
// Wallet (read-only for members)
// ---------------------------------------------------------------------------

export interface MemberWalletBalance {
  balance: string;
  currency: string;
}

export interface MemberWalletTransaction {
  id: string;
  kind: "credit" | "debit";
  source: string;
  amount: string;
  note: string;
  created_at: string;
}

export interface MemberWalletTransactionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  balance: string;
  currency: string;
  results: MemberWalletTransaction[];
}

export function memberWalletBalance(): Promise<MemberWalletBalance> {
  return memberFetch<MemberWalletBalance>("/member/wallet/balance/");
}

export function memberWalletTransactions(page = 1): Promise<MemberWalletTransactionsResponse> {
  return memberFetch<MemberWalletTransactionsResponse>(
    `/member/wallet/transactions/?page=${page}`,
  );
}

// --- Consent (member portal) ------------------------------------------------
// These mirror consent.ts but use memberFetch so the member JWT is sent.

import type {
  MemberConsentRecord,
  RecordConsentInput,
  ConsentPolicyDocument,
} from "@/lib/api/consent";

export function memberGetConsentState(memberId: string): Promise<MemberConsentRecord[]> {
  return memberFetch<MemberConsentRecord[]>(`/members/${memberId}/consents/`);
}

export function memberRecordConsent(
  memberId: string,
  data: RecordConsentInput,
): Promise<MemberConsentRecord> {
  return memberFetch<MemberConsentRecord>(`/members/${memberId}/consents/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function memberGetCurrentPolicyDocuments(): Promise<ConsentPolicyDocument[]> {
  return memberFetch<ConsentPolicyDocument[]>(`/consent/policy/`);
}
